import { useCallback, useContext, useMemo, useState } from "react";
import Button from "../../../UiLib/Button/Button";
import WidgetContainer from "../../../UiLib/WidgetContainer/WidgetContainer";
import TaskPriority from "../../../Shared/TaskPriority";
import TaskType from "../../../Shared/TaskType";
import TaskTags from "../TaskTags/TaskTags";
import { TaskContext } from "../../../../contexts/TaskContext";
import { Context } from "../../../..";
import useOutsideAlerter from "../../../../hooks/useOutsideAlert";
import { ReactComponent as BluePen } from "../../../../assets/editButtonGray.svg";
import { ReactComponent as Cross } from "../../../../assets/images/cross.svg";
import "./TaskAttributes.css";
import { AppContext } from "../../../../App";

// Типы для данных приоритета и типа
interface AttributeItem {
    id: number;
    name: string;
    color: string;
    current: boolean;
}

interface AttributeDisplayData {
    display: JSX.Element;
    opacity: React.CSSProperties;
}

const TaskAttributes = () => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    const taskContext = useContext(TaskContext);

    const [openWindows, setOpenWindows] = useState({
        priority: false,
        type: false
    });

    const outsideAlerterRefPriority = useOutsideAlerter<HTMLDivElement>(() =>
        setOpenWindows(prev => ({ ...prev, priority: false }))
    );

    const outsideAlerterRefType = useOutsideAlerter<HTMLDivElement>(() =>
        setOpenWindows(prev => ({ ...prev, type: false }))
    );

    const { taskInformation, editTask, updatePriority, updateType, addTags, removeTags } = taskContext;
    const currentProjectId = taskInformation?.projectId ?? store.currentProjectId;

    // Универсальная функция для переключения окон
    const toggleWindow = useCallback((window: keyof typeof openWindows) => {
        setOpenWindows(prev => ({ ...prev, [window]: !prev[window] }));
    }, []);

    // Обработчики кликов с useCallback
    const handlePriorityClick = useCallback((index: number) => {
        updatePriority(index);
        setOpenWindows(prev => ({ ...prev, priority: false }));
    }, [updatePriority]);

    const handleTypeClick = useCallback((index: number) => {
        updateType(index);
        setOpenWindows(prev => ({ ...prev, type: false }));
    }, [updateType]);

    // Универсальная функция для создания отображения атрибута
    const createAttributeDisplay = useCallback((
        item: AttributeItem,
        index: number,
        onClickHandler: (index: number) => void
    ) => {
        const style = JSON.parse(item?.color || '{}');
        const Component = onClickHandler === handlePriorityClick ? TaskPriority : TaskType;

        return (
            <button
                className="button__taskPriorityDisplay--chek"
                onClick={() => onClickHandler(index)}
                aria-label={`Изменить на ${item.name}`}
            >
                <Component name={item.name} color={style?.color} icon={style.icon} />
            </button>
        );
    }, [handlePriorityClick, handleTypeClick]);

    // Мемоизированные данные для приоритетов и типов
    const { priorityList, priorityCurrent } = useMemo(() => {
        const list = taskInformation?.priority?.map((priority, index) => ({
            ...priority,
            display: createAttributeDisplay(priority, index + 1, handlePriorityClick),
            opacity: priority.current ? {} : { opacity: 0.3 }
        })) || [];

        const current = list.find(item => item.current);

        return { priorityList: list, priorityCurrent: current };
    }, [taskInformation?.priority, createAttributeDisplay, handlePriorityClick]);

    const { typeList, typeCurrent } = useMemo(() => {
        const list = taskInformation?.type?.map((type, index) => ({
            ...type,
            display: createAttributeDisplay(type, index + 1, handleTypeClick),
            opacity: type.current ? {} : { opacity: 0.3 }
        })) || [];

        const current = list.find(item => item.current);

        return { typeList: list, typeCurrent: current };
    }, [taskInformation?.type, createAttributeDisplay, handleTypeClick]);

    // Получение текущего статуса
    const currentStatus = useMemo(() =>
        taskInformation?.status.find(status => status.current),
        [taskInformation?.status]
    );

    // Компонент для модального окна атрибутов
    const AttributeModal = useCallback(({
        isOpen,
        onClose,
        title,
        items,
        refItem
    }: {
        isOpen: boolean;
        onClose: () => void;
        title: string;
        items: AttributeDisplayData[];
        refItem: React.RefObject<HTMLDivElement>;
    }) => {
        if (!isOpen) return null;

        return (
            <div ref={refItem} className="task-attributes-modal">
                <WidgetContainer>
                    <div className="task-attributes-modal-head">
                        <a>{title}</a>
                        <Button
                            id="button-cross"
                            callBack={onClose}
                            image={<Cross />}
                        />
                    </div>
                    {items.map((item, index) => (
                        <div
                            key={`${title.toLowerCase()}-${index}`}
                            style={item.opacity}
                            className="task-attributes-body-row-value-type"
                        >
                            {item.display}
                        </div>
                    ))}
                </WidgetContainer>
            </div>
        );
    }, []);

    // Компонент для строки атрибута
    const AttributeRow = useCallback(({
        label,
        currentValue,
        onEdit,
        modalProps
    }: {
        label: string;
        currentValue?: AttributeDisplayData;
        onEdit: () => void;
        modalProps?: {
            isOpen: boolean;
            onClose: () => void;
            title: string;
            items: AttributeDisplayData[];
            refItem: React.RefObject<HTMLDivElement>;
        };
    }) => (
        <div className="task-attributes-body-row">
            <span className="task-attributes-body-row-head">{label}</span>
            <div className="task-attributes-body-row-value">
                {currentValue && (
                    <div
                        style={currentValue.opacity}
                        className={`task-attributes-body-row-value-${label.toLowerCase()}`}
                    >
                        {currentValue.display}
                    </div>
                )}
                <div className="task-attributes-button-edit">
                    <Button
                        id={editTask ? "button-pen" : "button-pen-gray"}
                        callBack={editTask ? onEdit : () => showToast("У вас нет прав на редактирование задачи")}
                        image={<BluePen />}
                    />
                    {modalProps && <AttributeModal {...modalProps} />}
                </div>
            </div>
        </div>
    ), [AttributeModal, editTask]);

    return (
        <div className="task-attributes-component">
            <div className="task-attributes-head">
                <h2>Атрибуты</h2>
            </div>

            <div className="task-attributes-body">
                {/* Статус */}
                <div className="task-attributes-body-row">
                    <a className="task-attributes-body-row-head">Статус:</a>
                    <a
                        style={{
                            color: currentStatus?.color ? JSON.parse(currentStatus.color)?.color : "inherit",
                            fontSize: "14px"
                        }}
                        className="task-attributes-body-row-value"
                    >
                        {currentStatus?.name}
                    </a>
                </div>

                {/* Приоритет */}
                <AttributeRow
                    label="Приоритет"
                    currentValue={priorityCurrent}
                    onEdit={() => toggleWindow('priority')}
                    modalProps={{
                        isOpen: openWindows.priority,
                        onClose: () => setOpenWindows(prev => ({ ...prev, priority: false })),
                        title: "Приоритет",
                        items: priorityList,
                        refItem: outsideAlerterRefPriority
                    }}
                />

                {/* Тип */}
                <AttributeRow
                    label="Тип"
                    currentValue={typeCurrent}
                    onEdit={() => toggleWindow('type')}
                    modalProps={{
                        isOpen: openWindows.type,
                        onClose: () => setOpenWindows(prev => ({ ...prev, type: false })),
                        title: "Тип",
                        items: typeList,
                        refItem: outsideAlerterRefType
                    }}
                />

                {/* Теги */}
                <div className="task-attributes-body-row-tags">
                    <span className="task-attributes-body-row-tags-head">Теги:</span>
                    <div className="task-attributes-body-row-value">
                        {taskInformation?.tags && (
                            <TaskTags
                                projectId={currentProjectId}
                                taskId={taskInformation.id}
                                tags={taskInformation.tags}
                                addTagsFunc={addTags}
                                removeTags={removeTags}
                                permission={editTask}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskAttributes;